// Ported from hotwire-flow (MIT, @hotwirebits/flow) into conek-ui. The engine
// logic is upstream's; identifiers/events/classes follow the conek contract in
// docs/superpowers/specs/2026-09-09-flow-graph-components-design.md §2.
import { Controller } from "@hotwired/stimulus"
import { resolveFlowEngine } from "../../../lib/flow/utils.js"

// Status bar — bottom bar showing zoom, node count, mouse position.
//
// Outlets (M4 fix) + mutation-driven counts (perf): the engine is resolved
// through the outlet attribute the component templates emit; counts update on
// membership events instead of a permanent 2s polling interval doing
// document-wide querySelectorAll scans. The legacy id lookup stays only as a
// resolution fallback (layout_controller still shares that utility).
export default class extends Controller {
  static targets = ["zoom", "position", "nodes", "edges"]
  static outlets = ["ui--flow"]

  static values = {
    // M7: count phrases are host-localizable. The component renders localized
    // initial text ("0 nodes" via I18n) but no template attribute, so the
    // default path only swaps the NUMBER in the existing text; hosts that
    // need a different phrase shape emit e.g.
    // data-ui--flow-status-bar-nodes-template-value="%{count} nœuds".
    nodesTemplate: { type: String, default: "" },
    edgesTemplate: { type: String, default: "" }
  }

  connect() {
    this._onMove = (e) => this._updatePosition(e)
    this.element.addEventListener("mousemove", this._onMove)

    this._onViewport = () => this._updateZoom()
    this._onMembership = () => this._updateCounts()
    this._events = [
      ["viewportchange", this._onViewport],
      ["ready", () => { this._updateZoom(); this._updateCounts() }],
      ["nodesadd", this._onMembership],
      ["nodedelete", this._onMembership],
      ["edgesadd", this._onMembership],
      ["edgedelete", this._onMembership],
      ["restore", this._onMembership]
    ]
    this._events.forEach(([name, handler]) => document.addEventListener(`ui--flow:${name}`, handler))

    // The pointer-coordinate readback is the bar's own mousemove — the bbox is
    // per-move getBoundingClientRect on flow.element in screenToFlow; nothing
    // to compute for the bar itself (it stays a plain span), so no cached
    // rect is needed here. The interval purge below is the main hot-path fix.
    // Wave-8 A3: engine-FIRST at connect (was _updateCountsDOM — its fallback
    // scope never contains the canvas's nodes, so the SSR-accurate localized
    // counts were clobbered to "0" until the retry healed them). _updateCounts
    // falls back to the DOM scan only when no engine resolves.
    this._updateCounts()
    this._updateZoom()

    // Outlet resolution is attribute-driven: one short retry covers host
    // markup with the identifier but a slow Stimulus upgrade; no permanent
    // polling (the old 2s setInterval ran forever and re-scanned the document
    // even at idle).
    this._retryTimer = setTimeout(() => {
      const flow = this._getFlow()
      if (flow) {
        this._updateZoom()
        this._updateCounts()
      }
    }, 500)

    // Chrome-insets contract (UX audit): the bar IS a full-width bottom
    // strip — announce arrival/removal so the engine re-measures insets.
    this._announceChrome()
  }

  disconnect() {
    this.element.removeEventListener("mousemove", this._onMove)
    this._events?.forEach(([name, handler]) => document.removeEventListener(`ui--flow:${name}`, handler))
    if (this._retryTimer) clearTimeout(this._retryTimer)
    this._announceChrome()
  }

  _announceChrome() {
    const selector = this.element.getAttribute("data-ui--flow-status-bar-ui--flow-outlet")
    const canvasId = selector && selector.startsWith("#") ? selector.slice(1) : null
    document.dispatchEvent(new CustomEvent("ui--flow:chromechange", { detail: { canvasId } }))
  }

  _getFlow() { return resolveFlowEngine(this) }

  _updatePosition(event) {
    if (!this.hasPositionTarget) return
    const flow = this._getFlow()
    if (!flow) return
    const pos = flow.screenToFlow(event.clientX, event.clientY)
    this.positionTarget.textContent = `${Math.round(pos.x)}, ${Math.round(pos.y)}`
  }

  _updateZoom() {
    if (!this.hasZoomTarget) return
    const flow = this._getFlow()
    if (!flow) return
    this._setCountText(this.zoomTarget, flow.zoomPercentage, "", `${flow.zoomPercentage}%`)
  }

  _updateCounts() {
    const flow = this._getFlow()
    if (flow) {
      // O(1) map sizes instead of document scans per membership event.
      if (this.hasNodesTarget) this._setCountText(this.nodesTarget, flow._nodes.size, this.nodesTemplateValue, `${flow._nodes.size} nodes`)
      if (this.hasEdgesTarget) this._setCountText(this.edgesTarget, flow._edges.size, this.edgesTemplateValue, `${flow._edges.size} edges`)
    } else {
      this._updateCountsDOM()
    }
  }

  // M7 copy: swap ONLY the number so the server-rendered localized phrase
  // survives ("0 nœuds" → "5 nœuds"). An explicit template value wins
  // ("%{count}" interpolation); a text with no digits gets the fallback.
  _setCountText(el, n, template, fallback) {
    if (template) { el.textContent = template.replace("%{count}", String(n)); return }
    const current = el.textContent || ""
    el.textContent = /\d+/.test(current) ? current.replace(/\d+/, String(n)) : fallback
  }

  // DOM fallback for hosts the outlet/id resolution could not reach — scoped
  // to this bar's CANVAS (wave-8 A3: the old parentElement scope was the
  // [data-flow-chrome] wrapper, which never contains the nodes — they live
  // under the sibling .ui-flow-pane — so the fallback found 0 nodes forever).
  // A bar mounted outside any canvas keeps the document.body scan.
  _updateCountsDOM() {
    const scope = this.element.closest("[data-controller~='ui--flow']") || document.body
    if (this.hasNodesTarget) {
      const n = scope.querySelectorAll("[data-ui--flow-node]").length
      this._setCountText(this.nodesTarget, n, this.nodesTemplateValue, `${n} nodes`)
    }
    if (this.hasEdgesTarget) {
      const n = scope.querySelectorAll("path.ui-flow-edge").length
      this._setCountText(this.edgesTarget, n, this.edgesTemplateValue, `${n} edges`)
    }
  }
}
